/**
 * The tenant's audit trail, newest first.
 *
 * Every row opens to its raw ids and metadata in the detail pane. A readiness
 * override opens by default, since its detail is why it is in the trail. Runs
 * of routine events fold into one row so they do not bury the decisions around
 * them.
 *
 * @module components/admin/AuditTrail
 */

import React, { type ReactNode, useState } from "react";

import { auditActorLabel } from "@/components/shared/audit-actor";
import { auditEventLabel } from "@/components/shared/audit-event-labels";
import {
  type AuditModuleName,
  type AuditNames,
  auditTargetLabel,
} from "@/components/shared/audit-target";
import {
  Button,
  EmptyState,
  ErrorAlert,
  Heading,
  MasterDetail,
  MasterItem,
  MasterList,
  MutedText,
  Pagination,
  Time,
  WarningAlert,
} from "@/components/ui";
import { Bone } from "@/components/ui/SkeletonParts";
import type { AuditEvent } from "@/server/types";

import { auditGroupLabel, type AuditRow, groupAuditRows } from "./audit-trail-rows";
import { AuditEventDetail, AuditGroupDetail, OVERRIDE_ACTION } from "./AuditEventDetail";

/** How many rows one page of the table holds. */
const PAGE_SIZE = 10;

const NO_MODULES: ReadonlyMap<string, AuditModuleName> = new Map();

export interface AuditTrailProps {
  events: readonly AuditEvent[];
  error: string | null;
  /** Reads the trail again. A failed read is the one state with no way on. */
  onRetry: () => void;
  pending: boolean;
  /** Actor id to display name, for the actors this tenant knows. */
  userNames: ReadonlyMap<string, string>;
  /** Target id to programme title, for the targets that are programmes. */
  programmeNames: ReadonlyMap<string, string>;
  /** Module id to title and programme, for module targets. */
  moduleNames?: ReadonlyMap<string, AuditModuleName>;
  /** Names a tenant or settings target. */
  tenantName?: string;
  /** Why the names are missing, when the workspace read that carries them failed. */
  namesError?: string;
  /** Reads the workspace again, for the names rather than the trail. */
  onRetryNames?: () => void;
  /** True when a filter narrows the trail, so an empty one says so. */
  filtered?: boolean;
  /** The filter row, kept above the list inside the master's surface. */
  filters?: ReactNode;
}

function firstEvent(row: AuditRow): AuditEvent {
  return row.kind === "event" ? row.event : (row.events[0] as AuditEvent);
}

function isOverride(row: AuditRow): boolean {
  return row.kind === "event" && row.event.action === OVERRIDE_ACTION;
}

function rowTestId(row: AuditRow): string {
  if (row.kind === "group") {
    return `audit-${row.key}`;
  }
  // Keyed like every other row: two overrides on one page shared one testid,
  // which `getByTestId` then refuses.
  return isOverride(row) ? `audit-readiness-override-${row.key}` : `audit-${row.key}`;
}

/**
 * @example
 * ```tsx
 * <AuditTrail events={events} error={error} pending={pending} onRetry={reload}
 *   userNames={names} programmeNames={titles} />
 * ```
 */
export function AuditTrail({
  events,
  error,
  pending,
  onRetry,
  userNames,
  programmeNames,
  moduleNames = NO_MODULES,
  tenantName = "This tenant",
  namesError = undefined,
  onRetryNames = undefined,
  filtered = false,
  filters = undefined,
}: AuditTrailProps): React.ReactElement {
  const [page, setPage] = useState(1);
  const [selectedKey, setSelectedKey] = useState<string | undefined>(undefined);
  // Grouped before paging, so a run never splits across two pages.
  const rows = groupAuditRows(events);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  // Clamped rather than reset: a shorter trail after a reload should show its
  // last page, not send the reader back to the first.
  const current = Math.min(page, pageCount);
  const visible = rows.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const selectedRow = visible.find((row) => row.key === selectedKey);
  const names: AuditNames = {
    users: userNames,
    programmes: programmeNames,
    modules: moduleNames,
    tenantName,
  };
  const actorLabel = (event: AuditEvent): string => auditActorLabel(event, userNames);
  const rowLabel = (row: AuditRow): string =>
    row.kind === "group" ? auditGroupLabel(row) : auditEventLabel(row.event.action);

  // Before the failure, not after it: a retry that leaves the same error on
  // screen tells the reader their press did nothing.
  const failure =
    !pending && error ? (
      <ErrorAlert data-testid="admin-audit-error">
        <span className="flex flex-wrap items-center gap-3">
          {error}
          <Button size="sm" variant="outline" onClick={onRetry}>
            Try again
          </Button>
        </span>
      </ErrorAlert>
    ) : undefined;

  function master(): React.ReactElement {
    if (pending) {
      return (
        <div role="status" aria-label="Loading audit trail" className="flex flex-col">
          {/* Each event's title over its time, actor and target. */}
          {Array.from({ length: 6 }, (_, index) => (
            <div
              key={`event-${String(index)}`}
              aria-hidden="true"
              className="flex flex-col gap-2 border-b border-base-300 p-3"
            >
              <Bone className="h-4 w-3/4" />
              <Bone className="h-3 w-1/2" />
            </div>
          ))}
        </div>
      );
    }
    if (failure) {
      return <div className="p-3">{failure}</div>;
    }
    if (rows.length === 0) {
      return (
        <div className="p-3">
          <EmptyState
            icon="clipboard-text"
            title={
              filtered
                ? "No audit events match these filters"
                : "No audit events are available for this tenant yet"
            }
            density="row"
          />
        </div>
      );
    }
    return (
      <MasterList>
        {visible.map((row) => {
          const actors = new Set((row.kind === "group" ? row.events : [row.event]).map(actorLabel));
          const actor = actors.size === 1 ? actorLabel(firstEvent(row)) : `${actors.size} people`;
          return (
            <MasterItem
              key={row.key}
              title={rowLabel(row)}
              meta={
                <>
                  <Time value={firstEvent(row).timestamp} /> · <span>{actor}</span>
                  {row.kind === "event" ? (
                    <>
                      {" · "}
                      <span>{auditTargetLabel(row.event, names)}</span>
                    </>
                  ) : null}
                </>
              }
              selected={row.key === selectedRow?.key}
              onSelect={() => {
                setSelectedKey(row.key);
              }}
              rowTestId={rowTestId(row)}
              data-testid={`${rowTestId(row)}-open`}
            />
          );
        })}
      </MasterList>
    );
  }
  return (
    <div className="space-y-4">
      {namesError ? (
        // Without this the rows read as a trail of raw ids with nothing saying
        // the names were ever meant to be there.
        <WarningAlert data-testid="admin-audit-names-error">
          <span className="flex flex-wrap items-center gap-3">
            People and programmes are shown by id: {namesError}
            {onRetryNames ? (
              <Button size="sm" variant="outline" onClick={onRetryNames}>
                Try again
              </Button>
            ) : null}
          </span>
        </WarningAlert>
      ) : null}
      <MasterDetail
        label="Audit events"
        selected={selectedRow !== undefined}
        onBack={() => {
          setSelectedKey(undefined);
        }}
        autoSelectFirst={{
          firstId: pending || failure ? undefined : (visible.find(isOverride) ?? visible[0])?.key,
          onSelect: setSelectedKey,
        }}
        masterHeader={filters}
        master={master()}
        masterFooter={
          pending || failure ? null : (
            <Pagination
              aria-label="Audit events"
              page={current}
              pageCount={pageCount}
              onPageChange={setPage}
            />
          )
        }
        detail={
          selectedRow ? (
            <div className="space-y-3" data-testid="audit-detail">
              <Heading level={2} size="md">
                {rowLabel(selectedRow)}
              </Heading>
              {selectedRow.kind === "event" ? (
                <MutedText as="p">
                  {actorLabel(selectedRow.event)} on {auditTargetLabel(selectedRow.event, names)},{" "}
                  <Time value={selectedRow.event.timestamp} zone />
                </MutedText>
              ) : null}
              {selectedRow.kind === "group" ? (
                <AuditGroupDetail row={selectedRow} actorLabel={actorLabel} />
              ) : (
                <AuditEventDetail event={selectedRow.event} />
              )}
            </div>
          ) : (
            <EmptyState
              icon="clipboard-text"
              title="Select an event to see its detail"
              density="pane"
            />
          )
        }
        data-testid="admin-audit-trail"
      />
    </div>
  );
}
